/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useState, useEffect} from 'react'
import {useParams, useSearchParams, useNavigate} from 'react-router-dom'
import {updatePasswordReset, verifyPasswordResetToken} from '../core/_requests'
import {useAuth} from '../core/Auth'
import clsx from 'clsx'
import {toAbsoluteUrl} from 'src/_metronic/helpers'

interface PasswordRequirement {
  id: string
  label: string
  regex: RegExp
  met: boolean
}

const PasswordReset = () => {
  const {token} = useParams<{token: string}>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const {logout} = useAuth()
  const resetToken = token || searchParams.get('token')

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [tokenValid, setTokenValid] = useState<boolean | null>(null)
  const [verifyingToken, setVerifyingToken] = useState(true)
  const [verificationData, setVerificationData] = useState<{
    passwordResetType?: string
    resetToken?: string
    userName?: string
  } | null>(null)

  const [requirements, setRequirements] = useState<PasswordRequirement[]>([
    {
      id: 'length',
      label: 'Password must be minimum 8 characters',
      regex: /.{8,}/,
      met: false
    },
    {
      id: 'alphanumeric',
      label: 'Combination of alphabet and number',
      regex: /(?=.*[a-zA-Z])(?=.*\d)/,
      met: false
    },
    {
      id: 'uppercase',
      label: 'At least one Upper case character',
      regex: /[A-Z]/,
      met: false
    },
    {
      id: 'special',
      label: 'At least one special character',
      regex: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
      met: false
    }
  ])

  // Validate password against requirements
  useEffect(() => {
    setRequirements(prevReqs =>
      prevReqs.map(req => ({
        ...req,
        met: req.regex.test(password)
      }))
    )
  }, [password])

  // Verify token on component mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!resetToken) {
        setTokenValid(false)
        setVerifyingToken(false)
        return
      }

      try {
        const response = await verifyPasswordResetToken(resetToken)
        setTokenValid(true)
        setVerificationData(response)
      } catch (err: any) {
        setTokenValid(false)
      } finally {
        setVerifyingToken(false)
      }
    }

    verifyToken()
  }, [resetToken])

  const allRequirementsMet = requirements.every(req => req.met)
  const passwordsMatch = password && confirmPassword && password === confirmPassword

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validations
    if (!resetToken) {
      setError('Invalid reset link. Please request a new password reset.')
      return
    }

    if (!verificationData?.userName) {
      setError('Username information is missing. Please request a new password reset.')
      return
    }

    if (!allRequirementsMet) {
      setError('Password does not meet all requirements.')
      return
    }

    if (!passwordsMatch) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)
    try {
      await updatePasswordReset(verificationData.userName, password, verificationData.resetToken || resetToken)
      setSuccess(true)
      setPassword('')
      setConfirmPassword('')
      
      // Logout the user and redirect to login
      await logout()
      setTimeout(() => {
        navigate('/auth/login')
      }, 2000)
    } catch (err: any) {
      setError(err?.response?.data?.responseMsg || 'Failed to reset password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (!resetToken) {
    return (
      <div
        id='loginBg'
        className='d-flex flex-column flex-column-fluid bgi-position-center bgi-no-repeat bgi-size-cover bgi-attachment-fixed'
        style={{
          backgroundImage: `url(${toAbsoluteUrl('/media/logos/authBg.jpeg')})`,
          minHeight: '100vh'
        }}
      >
        <div className='d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20'>
          <div className='w-100' style={{maxWidth: '480px'}}>
            <div className='bg-white rounded shadow-lg px-10 px-lg-15 pb-10 mx-auto'>
              <div className='text-center mb-4'>
                <img 
                  src={toAbsoluteUrl('/media/logos/def-full.png')}
                  alt='logo' 
                  className='mb-4'
                  style={{height: '80px', width: 'auto'}}
                />
              </div>
              <div className='alert alert-danger mt-5 mb-0'>
                The reset password link invalid. Please contact customer service helpdesk
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (verifyingToken) {
    return (
      <div
        id='loginBg'
        className='d-flex flex-column flex-column-fluid bgi-position-center bgi-no-repeat bgi-size-cover bgi-attachment-fixed'
        style={{
          backgroundImage: `url(${toAbsoluteUrl('/media/logos/authBg.jpeg')})`,
          minHeight: '100vh'
        }}
      >
        <div className='d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20'>
          <div className='w-100' style={{maxWidth: '480px'}}>
            <div className='bg-white rounded shadow-lg px-10 px-lg-15 pb-10 mx-auto'>
              <div className='text-center mb-4'>
                <img 
                  src={toAbsoluteUrl('/media/logos/def-full.png')}
                  alt='logo' 
                  className='mb-4'
                  style={{height: '80px', width: 'auto'}}
                />
              </div>
              <div className='text-center mt-5'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
                <p className='text-muted mt-3'>Verifying your reset link...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (tokenValid === false) {
    return (
      <div
        id='loginBg'
        className='d-flex flex-column flex-column-fluid bgi-position-center bgi-no-repeat bgi-size-cover bgi-attachment-fixed'
        style={{
          backgroundImage: `url(${toAbsoluteUrl('/media/logos/authBg.jpeg')})`,
          minHeight: '100vh'
        }}
      >
        <div className='d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20'>
          <div className='w-100' style={{maxWidth: '480px'}}>
            <div className='bg-white rounded shadow-lg px-10 px-lg-15 pb-10 mx-auto'>
              <div className='text-center mb-4'>
                <img 
                  src={toAbsoluteUrl('/media/logos/def-full.png')}
                  alt='logo' 
                  className='mb-4'
                  style={{height: '80px', width: 'auto'}}
                />
              </div>
              <div className='alert alert-danger mt-5 mb-0'>
                The reset password link invalid. Please contact customer service helpdesk
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      id='loginBg'
      className='d-flex flex-column flex-column-fluid bgi-position-center bgi-no-repeat bgi-size-cover bgi-attachment-fixed'
      style={{
        backgroundImage: `url(${toAbsoluteUrl('/media/logos/authBg.jpeg')})`,
        minHeight: '100vh'
      }}
    >
      <div className='d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20'>
        <div className='w-100' style={{maxWidth: '500px'}}>
          <div className='bg-white rounded shadow-lg px-10 px-lg-15 pb-10 mx-auto'>
            <div className='text-center mb-5 mt-5'>
              <img 
                src={toAbsoluteUrl('/media/logos/def-full.png')}
                alt='logo' 
                className='mb-4'
                style={{height: '80px', width: 'auto'}}
              />
              <h2 className='fw-bold mb-2'>Reset Your Password</h2>
              <p className='text-muted'>Enter your new password below</p>
            </div>

            {success && (
              <div className='alert alert-success mb-4'>
                <strong>Success!</strong> Password reset successfully. Redirecting to login...
              </div>
            )}

            {error && (
              <div className='alert alert-danger mb-4'>
                <strong>Error:</strong> {error}
              </div>
            )}

            <div className='mb-5 p-4 border rounded' style={{backgroundColor: '#f8f9fa'}}>
              <p className='fw-bold mb-3'>To set new password, you must comply with the password policy below:</p>
              <ul className='list-unstyled'>
                {requirements.map(req => (
                  <li key={req.id} className='mb-2 d-flex align-items-center'>
                    <div 
                      className={clsx('me-3 d-flex align-items-center justify-content-center rounded-circle', {
                        'bg-success': req.met,
                        'bg-light border': !req.met
                      })}
                      style={{width: '28px', height: '28px', minWidth: '28px'}}
                    >
                      {req.met && (
                        <i className='bi bi-check-lg text-white fw-bold'></i>
                      )}
                    </div>
                    <span className={clsx('flex-grow-1', {
                      'text-muted text-decoration-line-through': req.met
                    })}>
                      {req.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <form onSubmit={handleSubmit}>
              <div className='mb-4 fv-row'>
                <label className='form-label fw-bold mb-2'>New Password</label>
                <input
                  type='password'
                  className='form-control form-control-lg'
                  placeholder='Enter new password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading || success}
                  autoFocus
                />
              </div>

              <div className='mb-5 fv-row'>
                <label className='form-label fw-bold mb-2'>Confirm Password</label>
                <input
                  type='password'
                  className={clsx('form-control form-control-lg', {
                    'is-invalid': confirmPassword && !passwordsMatch,
                    'is-valid': confirmPassword && passwordsMatch && allRequirementsMet
                  })}
                  placeholder='Confirm new password'
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading || success}
                />
                {confirmPassword && !passwordsMatch && (
                  <div className='invalid-feedback d-block mt-2'>
                    <i className='bi bi-exclamation-circle me-1'></i>
                    Passwords do not match.
                  </div>
                )}
                {confirmPassword && passwordsMatch && allRequirementsMet && (
                  <div className='valid-feedback d-block mt-2'>
                    <i className='bi bi-check-circle me-1'></i>
                    Passwords match and meet all requirements.
                  </div>
                )}
              </div>

              <button
                type='submit'
                className='btn btn-primary btn-lg w-100 mb-5'
                disabled={!allRequirementsMet || !passwordsMatch || loading || success}
                style={{
                  backgroundColor: '#00a7e1',
                  borderColor: '#00a7e1',
                  borderRadius: '8px'
                }}
              >
                {loading ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                    Processing...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PasswordReset
